"use client";
import { Trigger } from "@radix-ui/react-select";
import { ArrowDown2 } from "iconsax-reactjs";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import {
  Controller,
  type FieldPath,
  type FieldValues,
  useFormContext,
} from "react-hook-form";

import { Select, SelectContent, SelectItem } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { FloatingLabelButton } from "../buttons/floating-label-button";

// ----------------------------------------------------------------------

type SelectOption = {
  label: ReactNode;
  value: string;
};

interface CustomRHFFloatingSelectProps<TFieldValues extends FieldValues>
  extends Omit<
      ComponentPropsWithoutRef<typeof Controller>,
      "name" | "control" | "render"
    >,
    Omit<ComponentPropsWithoutRef<typeof Select>, "name" | "defaultValue"> {
  name: FieldPath<TFieldValues>;
  helperText?: string;
  containerClass?: string;
  label?: string;
  buttonClass?: string;
  options: SelectOption[];
  emptyText?: string;
  fieldsetClassName?: string;
  selectContentClassName?: string;
  selectItemClassName?: string;
}

export default function CustomRHFFloatingSelect<
  TFieldValues extends FieldValues,
>({
  name,
  label,
  helperText,
  defaultValue,
  rules,
  containerClass,
  buttonClass,
  options,
  emptyText,
  fieldsetClassName,
  selectContentClassName,
  selectItemClassName,
}: CustomRHFFloatingSelectProps<TFieldValues>) {
  const { control } = useFormContext();

  const handleValueChange = (
    value: string,
    onChange: (value: string) => void,
  ) => {
    if (value !== "empty") {
      onChange(value);
      return;
    }
    onChange("");
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules}
      render={({ field, fieldState: { error } }) => {
        // Find the selected option's label based on the field value
        const selectedOption = options.find((opt) => opt.value === field.value);
        const displayValue = selectedOption?.label || field.value;

        return (
          <div className={cn("space-y-2", containerClass)}>
            <Select
              onValueChange={(value) =>
                handleValueChange(value, field.onChange)
              }
              defaultValue={field.value}
            >
              <Trigger asChild>
                <FloatingLabelButton
                  fieldsetClassName={fieldsetClassName}
                  label={label ?? ""}
                  value={displayValue}
                  variant="outline"
                  className={cn("w-full text-primary", buttonClass)}
                  endIcon={
                    <ArrowDown2
                      size={20}
                      className="ms-2 h-4 w-4 shrink-0 opacity-50"
                    />
                  }
                  error={!!error}
                  size="sm"
                />
              </Trigger>
              <SelectContent
                className={cn(
                  "bg-bg-secondary rounded-sm border-stroke-light shadow-[0px_2px_4px_-2px_#0000001A,0px_4px_6px_-1px_#0000001A]",
                  selectContentClassName,
                )}
              >
                <SelectItem
                  value="empty"
                  className={cn(
                    "rounded-sm focus:bg-bg-tertiary/60 outline-none",
                    selectItemClassName,
                  )}
                >
                  <span className="text-primary">{emptyText}</span>
                </SelectItem>
                {options.map((o, idx) => (
                  <SelectItem
                    key={`${o.value}-${idx}`}
                    value={o.value}
                    className={cn(
                      "font-medium rounded-sm",
                      selectItemClassName,
                    )}
                  >
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!!helperText && (
              <p className="text-sm text-muted-foreground ms-1.5">
                {helperText}
              </p>
            )}
            {!!error && (
              <p className="text-sm text-error ms-1.5">{error?.message}</p>
            )}
          </div>
        );
      }}
    />
  );
}
